import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import "./DraftPicks.css";
import DraftCalculator from "./DraftCalculator";
import {
  TEAMS_PER_ROUND,
  TOTAL_PICKS,
  TEAM_ORDER,
  TRADES,
  pickStringToOverall,
} from "../data/leagueData";
import { ADP_BY_OVERALL } from "../data/adp";

/* ---------------- constants ---------------- */
const VALID_TABS = ["Picks", "Teams", "Trades", "Calc", "Sheet"];
// use imported TOTAL_PICKS and TEAMS_PER_ROUND directly

/* ---------------- data (static) ---------------- */
const teams = ["All Teams", ...TEAM_ORDER];

/* ---------------- helpers ---------------- */
const sanitizeTab = (val) => {
  if (!val) return "Picks";
  const normalized =
    val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
  return VALID_TABS.includes(normalized) ? normalized : "Picks";
};

const buildBasePicks = () => {
  const arr = new Array(TOTAL_PICKS);
  for (let i = 0; i < TOTAL_PICKS; i++) {
    const round = Math.floor(i / TEAMS_PER_ROUND) + 1;
    const pickInRound = (i % TEAMS_PER_ROUND) + 1;
    const overall = i + 1;
    const teamIndex =
      round % 2 === 0 ? TEAMS_PER_ROUND - pickInRound : pickInRound - 1;
    const name = TEAM_ORDER[teamIndex];

    arr[i] = {
      number: `${round}.${pickInRound}`,
      overall,
      team: name,
      originalTeam: name,
      traded: false,
      from: null,
      // chronological list of owners starting with original and ending with current
      owners: [name],
      tradeId: undefined,
    };
  }
  return arr;
};

// Helper function to get players around a specific pick
const getPlayersAroundPick = (overall) => {
  const players = [];
  const start = Math.max(1, overall - 2);
  const end = Math.min(204, overall + 2);
  
  for (let i = start; i <= end; i++) {
    const player = ADP_BY_OVERALL.get(i);
    if (player) {
      players.push({
        ...player,
        isPrimary: i === overall
      });
    }
  }
  
  return players;
};

/* ---------------- shared UI: PopoverSelect ---------------- */
function PopoverSelect({
  label = "Select",
  value,
  options,
  onChange,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const handlePick = (val) => {
    onChange(val);
    close();
  };

  return (
    <div className="psel">
      <button
        type="button"
        className={`psel-btn ${value !== options[0] ? "psel-btn--active" : ""}`}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel || label}
        title={label}
      >
        <span className="psel-text">{value}</span>
        <svg
          className={`psel-caret ${open ? "is-open" : ""}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="M6 9l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="psel-pop" role="menu" ref={popRef}>
          <div className="psel-list">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                role="menuitem"
                className={`psel-item ${value === opt ? "is-active" : ""}`}
                onClick={() => handlePick(opt)}
              >
                {opt}
              </button>
            ))}
            {value !== options[0] && (
              <button
                type="button"
                className="psel-clear"
                onClick={() => handlePick(options[0])}
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Player Tooltip Component ---------------- */
function PlayerTooltip({ pick, onClose, onShowPlayers }) {
  const tooltipRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [onClose]);

  const primaryPlayer = ADP_BY_OVERALL.get(pick.overall);
  
  if (!primaryPlayer) {
    return (
      <div className="player-tooltip" ref={tooltipRef}>
        <div className="tooltip-content">
          <p>No ADP data available for this pick</p>
          <button className="tooltip-close" onClick={onClose}>×</button>
        </div>
      </div>
    );
  }

  return (
    <div className="player-tooltip" ref={tooltipRef}>
      <div className="tooltip-content">
        <div className="tooltip-player">
          <strong>{primaryPlayer.name}</strong>
          <span className="player-details">
            {primaryPlayer.position} - {primaryPlayer.team}
          </span>
          <span className="player-adp">
            ADP: {primaryPlayer.adp_formatted}
          </span>
        </div>
        <button 
          className="tooltip-expand-btn"
          onClick={() => onShowPlayers(pick.overall)}
        >
          Show players around this pick
        </button>
        <button className="tooltip-close" onClick={onClose}>×</button>
      </div>
    </div>
  );
}

/* ---------------- Players List Component ---------------- */
function PlayersList({ overall, onClose }) {
  const playersListRef = useRef(null);
  const players = getPlayersAroundPick(overall);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (playersListRef.current && !playersListRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [onClose]);

  return (
    <div className="players-list-overlay">
      <div className="players-list" ref={playersListRef}>
        <div className="players-list-header">
          <h3>Players around pick #{overall}</h3>
          <button className="players-list-close" onClick={onClose}>×</button>
        </div>
        <div className="players-list-content">
          {players.map((player) => (
            <div 
              key={player.overall} 
              className={`player-item ${player.isPrimary ? 'primary-player' : ''}`}
            >
              <div className="player-rank">#{player.overall}</div>
              <div className="player-info">
                <div className="player-name">
                  {player.isPrimary ? <strong>{player.name}</strong> : player.name}
                </div>
                <div className="player-details">
                  {player.position} - {player.team}
                </div>
              </div>
              <div className="player-adp">{player.adp_formatted}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



/* ---------------- main ---------------- */
export default function DraftPicks() {
  /* ---------- mark Draft tab active + dot (no navbar changes needed) ---------- */
  useEffect(() => {
    document.body.classList.add("is-draft-page");
    return () => document.body.classList.remove("is-draft-page");
  }, []);

  /* ---------- URL tab ---------- */
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = sanitizeTab(searchParams.get("tab"));
  const [view, setView] = useState(urlTab);

  // ensure URL always has valid ?tab= without piling history
  useEffect(() => {
    if (urlTab !== searchParams.get("tab")) {
      const sp = new URLSearchParams(searchParams);
      sp.set("tab", urlTab);
      setSearchParams(sp, { replace: true });
    }
  }, [urlTab, searchParams, setSearchParams]);

  // sync local view if nav updates URL
  useEffect(() => {
    const current = sanitizeTab(searchParams.get("tab"));
    if (current !== view) setView(current);
  }, [searchParams, view]);

  /* ---------- picks (memoized) ---------- */
  const picks = useMemo(() => {
    const base = buildBasePicks();

    // map pick number -> index for O(1) updates
    const indexByNumber = new Map();
    for (let i = 0; i < base.length; i++) {
      indexByNumber.set(base[i].number, i);
    }

    // apply trades from shared source
    for (const tr of TRADES) {
      for (const pickNumber of tr.picks) {
        const idx = indexByNumber.get(pickNumber);
        if (idx != null) {
          const p = base[idx];
          base[idx] = {
            ...p,
            team: tr.to,
            traded: true,
            from: tr.from,
            owners: [...(p.owners || [p.originalTeam]), tr.to],
            tradeId: tr.tradeId,
          };
        }
      }
    }

    return base;
  }, []);

  /* ---------- tooltip state ---------- */
  const [tooltipPick, setTooltipPick] = useState(null);
  const [showPlayersList, setShowPlayersList] = useState(false);
  const [selectedOverall, setSelectedOverall] = useState(null);

  const handleRowClick = useCallback((pick) => {
    setTooltipPick(pick);
    setShowPlayersList(false);
  }, []);

  const handleCloseTooltip = useCallback(() => {
    setTooltipPick(null);
  }, []);

  const handleShowPlayers = useCallback((overall) => {
    setSelectedOverall(overall);
    setShowPlayersList(true);
    setTooltipPick(null);
  }, []);

  const handleClosePlayersList = useCallback(() => {
    setShowPlayersList(false);
    setSelectedOverall(null);
  }, []);

  // Render helpers
  const renderOriginOwners = useCallback((pick) => {
    const owners = pick?.owners || [];
    if (!pick?.traded || owners.length <= 1) return null; // blank if never traded
    const previousOwners = owners.slice(0, -1); // exclude current owner
    if (previousOwners.length === 0) return null;
    return (
      <div className="origin-owners" role="group" aria-label="Previous owners">
        <div className="owner-badges">
          {previousOwners.map((ownerName, idx) => {
            const isLast = idx === previousOwners.length - 1;
            return (
              <React.Fragment key={`${pick.overall}-owner-${idx}`}>
                <button
                  type="button"
                  className={`owner-badge ${isLast ? "is-expanded" : "is-collapsed"}`}
                  title={ownerName}
                  tabIndex={0}
                >
                  <span className="owner-name">{ownerName}</span>
                </button>
                {!isLast && (
                  <span className="owner-arrow" aria-hidden="true">→</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }, []);

  const handlePickClick = useCallback((pickNumber) => {
    // Convert pick number (e.g., "1.7" or "1.07") to overall number using helper function
    const overall = pickStringToOverall(pickNumber);
    
    if (overall && overall >= 1 && overall <= 204) {
      // Create a mock pick object for the tooltip
      const mockPick = {
        overall,
        number: pickNumber,
        team: 'Trade Pick',
        traded: true,
        from: null,
        tradeId: undefined
      };
      setTooltipPick(mockPick);
      setShowPlayersList(false);
    }
  }, []);

/* ---------- Teams view ---------- */
const [selectedTeam, setSelectedTeam] = useState("");
const handleTeamChange = useCallback((val) => setSelectedTeam(val), []);

// Pick a random team (excluding "All Teams") on load
useEffect(() => {
  const teamList = teams.slice(1); // remove "All Teams"
  const random = teamList[Math.floor(Math.random() * teamList.length)];
  setSelectedTeam(random);
}, []);

  /* ---------- Trades controls: sort by tradeId + team filter ---------- */
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc' (desc=newest first)
  const [teamFilter, setTeamFilter] = useState("All Teams");

  const toggleSortDir = useCallback(
    () => setSortDir((s) => (s === "desc" ? "asc" : "desc")),
    []
  );

  // build trade groups (filtered by team, sorted by tradeId)
  const tradeGroups = useMemo(() => {
    const ids = Array.from(new Set(TRADES.map((t) => t.tradeId)));

    // filter by team if needed
    const filteredIds =
      teamFilter === "All Teams"
        ? ids
        : ids.filter((id) => {
            const rows = TRADES.filter((t) => t.tradeId === id);
            return rows.some((r) => r.from === teamFilter || r.to === teamFilter);
          });

    // sort by tradeId numeric
    filteredIds.sort((a, b) => (sortDir === "asc" ? a - b : b - a));

    // shape groups (use first row date for display)
    return filteredIds.map((id) => {
      const rows = TRADES.filter((t) => t.tradeId === id);
      const date = rows[0]?.date ?? "";
      return { id, date, rows };
    });
  }, [sortDir, teamFilter]);

  /* ---------- render ---------- */
  return (
    <div className="draft-picks-container">

      {/* Picks */}
      {view === "Picks" && (
        <div className="picks-page">
          <div className="picks-hero">
            <div className="hero-content">
              <h1 className="hero-title">Draft Board</h1>
              <p className="hero-subtitle">Complete overview of all 204 picks in the 2025 draft</p>
            </div>
          </div>
          
          <div className="picks-container">
            <table className="draft-picks-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Pick</th>
                  <th>Team</th>
                  <th>Origin</th>
                </tr>
              </thead>
              <tbody>
                {picks.map((pick) => (
                  <tr
                    key={pick.overall}
                    className={`${
                      Math.floor((pick.overall - 1) / TEAMS_PER_ROUND) % 2 === 0
                        ? "even-round"
                        : "odd-round"
                    } clickable-row`}
                    onClick={() => handleRowClick(pick)}
                  >
                    <td className="overall">{pick.overall}</td>
                    <td className="number">{pick.number}</td>
                    <td className="team">{pick.team}</td>
                    <td className="origin">{renderOriginOwners(pick)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Teams */}
      {view === "Teams" && (
        <div className="teams-page">
          <div className="teams-hero">
            <div className="hero-content">
              <h1 className="hero-title">Team Picks</h1>
              <p className="hero-subtitle">View picks by individual team</p>
            </div>
          </div>
          
          <div className="teams-container">
            <div className="teams-controls">
              <PopoverSelect
                label="Filter by team"
                ariaLabel="Filter by team"
                value={selectedTeam}
                options={TEAM_ORDER} // no "All Teams" in the dropdown
                onChange={handleTeamChange}
              />
            </div>

            <table className="team-picks-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Pick</th>
                  <th>Origin</th>
                </tr>
              </thead>
              <tbody>
                {picks
                  .filter((p) => p.team === selectedTeam) // no "All Teams" check needed
                  .map((pick, idx) => (
                    <tr
                      key={`${pick.overall}-${idx}`}
                      className={`${
                        Math.floor((pick.overall - 1) / TEAMS_PER_ROUND) % 2 === 0
                          ? "even-round"
                          : "odd-round"
                      } ${idx === 7 ? "ninth-row" : ""} clickable-row`}
                      onClick={() => handleRowClick(pick)}
                    >
                      <td className="overall">{pick.overall}</td>
                      <td className="number">{pick.number}</td>
                      <td className="team">{renderOriginOwners(pick)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Trades */}
      {view === "Trades" && (
        <div className="trades-page">
          <div className="trades-hero">
            <div className="hero-content">
              <h1 className="hero-title">Trade History</h1>
              <p className="hero-subtitle">Track all the moves that shape the league's landscape</p>
            </div>
          </div>
          
          <div className="trades-container">
              <div className="trades-controls">
              <button
                type="button"
                className="sort-button sort-button--icon"
                onClick={toggleSortDir}
                aria-label={`Sort trades ${
                  sortDir === "desc"
                    ? "descending (newest first)"
                    : "ascending (oldest first)"
                }`}
                title="Sort by trade #"
              >
                <span className="sort-icon" aria-hidden="true">
                  {sortDir === "desc" ? (
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path d="M12 20V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M7 9l5-5 5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path d="M12 4v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M17 15l-5 5-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
              </button>

              <div className="filter-control">
                <PopoverSelect
                  label="Filter by team"
                  ariaLabel="Filter by team"
                  value={teamFilter}
                  options={teams}
                  onChange={setTeamFilter}
                />
              </div>
            </div>

            <div className="trades-grid">
              {tradeGroups.map((g) => (
                <div key={g.id} className="trade-card">
                  <div className="trade-header">
                    <div className="trade-number">
                      <span className="trade-label">Trade</span>
                      <span className="trade-id">#{g.id}</span>
                    </div>
                    <div className="trade-date">{g.date}</div>
                  </div>
                  
                  <div className="trade-partners">
                    {g.rows.map((row, i) => (
                      <div key={i} className="trade-partner">
                        <div className="partner-info">
                          <div className="partner-name">{row.from}</div>
                          <div className="trade-action">sends</div>
                          <div className="partner-picks">
                            {row.picks.map((pick, pickIndex) => (
                              <span 
                                key={pickIndex} 
                                className="pick-badge clickable-pick"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePickClick(pick);
                                }}
                              >
                                {pick}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {tradeGroups.length === 0 && (
                <div className="trades-empty">
                  <div className="empty-content">
                    <div className="empty-icon">🤝</div>
                    <h3 className="empty-title">No Trades Yet</h3>
                    <p className="empty-message">The league is still in the early stages. Trades will appear here once they happen!</p>
                    <div className="empty-gif">
                      <iframe
                        src="https://giphy.com/embed/C4etHHKiPgDqKCPP0m"
                        width="100%"
                        height="200"
                        style={{ border: "none", maxWidth: "300px" }}
                        allowFullScreen
                        title="No trades yet GIF"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Calc (unchanged component, but PopoverSelect is available to reuse there) */}
      {view === "Calc" && <DraftCalculator />}

      {/* Official Google Sheet */}
      {view === "Sheet" && (
        <div className="sheet-page">
          <div className="sheet-hero">
            <div className="hero-content">
              <h1 className="hero-title">Official Google Sheet</h1>
              <p className="hero-subtitle">Live league spreadsheet</p>
            </div>
          </div>
          <div className="sheet-container">
            <div className="sheet-actions">
              <a
                className="button btn-accent btn-pill"
                href="https://docs.google.com/spreadsheets/d/e/2PACX-1vR0f3eQJ29zv-Iz8I4OUvWG4_5KKACh7uqbyhEr3d6MkLb4Snqy_udlCuWJfbNclU80QZN6kri1vUDR/pubhtml"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click to see the full sheet ›
              </a>
            </div>
            <iframe
              className="sheet-embed"
              src="https://docs.google.com/spreadsheets/d/e/2PACX-1vR0f3eQJ29zv-Iz8I4OUvWG4_5KKACh7uqbyhEr3d6MkLb4Snqy_udlCuWJfbNclU80QZN6kri1vUDR/pubhtml?widget=true&headers=false"
              title="Official Google Sheet"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltipPick && (
        <PlayerTooltip 
          pick={tooltipPick} 
          onClose={handleCloseTooltip}
          onShowPlayers={handleShowPlayers}
        />
      )}



      {/* Players List */}
      {showPlayersList && selectedOverall && (
        <PlayersList 
          overall={selectedOverall} 
          onClose={handleClosePlayersList}
        />
      )}
    </div>
  );
}